/**
 * Firebase Configuration
 * Uses environment variables for security
 * See .env.example for required variables
 */
import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
];

const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);
if (missingVars.length > 0) {
  console.error(`Missing Firebase environment variables: ${missingVars.join(', ')}`);
  console.error('Please create a .env.local file based on .env.example');
}

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// initializeFirestore (vs getFirestore) lets us tune the transport.
// experimentalAutoDetectLongPolling probes the network and picks the
// fastest available transport (WebChannel streaming when possible,
// long-polling fallback otherwise) without the slow default fallback
// dance that adds ~1s of latency on the first Firestore op.
//
// persistentLocalCache hydrates from IndexedDB on repeat visits — the
// menu paints instantly while the SDK revalidates in the background.
// persistentMultipleTabManager keeps multiple tabs consistent without
// the old "only one tab can use persistence" limitation.
const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
const functions = getFunctions(app);

// Connect to Functions emulator only (Firestore stays in production)
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.log('🔧 Using Functions Emulator (Firestore: Production)');
}

// Lazy Firebase Analytics loader — keeps the Analytics SDK (~86 KB) out of
// the critical path. Skips entirely when VITE_FIREBASE_MEASUREMENT_ID is
// unset (so staging/dev don't ship gtag with `id=undefined`).
let analyticsPromise = null;
export function loadAnalyticsDeferred() {
  if (analyticsPromise) return analyticsPromise;
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
  if (!measurementId) {
    analyticsPromise = Promise.resolve(null);
    return analyticsPromise;
  }
  analyticsPromise = (async () => {
    try {
      const { getAnalytics, isSupported } = await import('firebase/analytics');
      if (!(await isSupported())) return null;
      return getAnalytics(app);
    } catch {
      return null;
    }
  })();
  return analyticsPromise;
}

export { app, db, functions };
